// Admin entity-management API client (Step 5e Phase 7). Admin-only — the server enforces
// requireRole('admin'); this lib never assumes the caller is admin (RequireRole + the gated
// nav entry hide the UI). All calls go through apiFetch (envelope + 401 silent-refresh).

import { apiFetch } from './api';

export type Result<T> = { data: T | null; error: string | null; warnings: string[] };
function unwrap<T>(r: { data: T | null; error: { message: string } | null; warnings: string[] }): Result<T> {
  return r.error ? { data: null, error: r.error.message, warnings: r.warnings } : { data: r.data, error: null, warnings: r.warnings };
}

// ── Coded vocabularies (mirror the backend deactivation schemas) ───────────────
export const AIRCRAFT_ASSET_REASONS = ['sold', 'retired', 'leased_out', 'restructure', 'other'] as const;
export const STAFF_REASONS = ['left_company', 'restructure', 'other'] as const;
export const BASE_REASONS = ['restructure', 'other'] as const;

// ── Types (list-row shapes returned by the list endpoints) ─────────────────────
export interface AdminAircraft {
  registration: string; model: string | null; aircraft_category: string;
  base_icao: string | null; current_base_icao: string | null;
  availability_mode: string; approval_required: boolean; approval_contact: string | null;
  capability_normal: string | null; capability_current: string | null; serviceable: boolean;
  status_note: string | null; expected_return_date: string | null;
  is_active: boolean; deactivated_at: string | null; deactivation_reason: string | null;
}
export interface AdminAsset {
  id: string; name: string; short_code: string | null; category: string | null; ui_color: string | null;
  base_id: string; capacity: number | null; ownership: string; availability_mode: string;
  approval_required: boolean; require_booking_confirmation: boolean; operating_hours: unknown;
  timezone: string | null; ppr_required: boolean;
  is_active: boolean; deactivated_at: string | null; deactivation_reason: string | null;
}
export interface AdminStaff {
  id: string; full_name: string; preferred_name: string | null; role: string | null;
  home_base_id: string | null; current_base_id: string | null; approval_required: boolean;
  is_active: boolean; deactivated_at: string | null; deactivation_reason: string | null;
}
export interface AdminStudent {
  id: string; full_name: string; first_name: string | null; last_name: string | null; company: string | null;
  preferred_name: string | null; training_stage: string | null;
  home_base_id: string | null; kind: 'student' | 'client';
  is_active: boolean; deactivated_at: string | null; deactivation_reason: string | null;
}
export interface AdminBase {
  id: string; name: string; short_code: string; ui_color: string; icao: string | null;
  timezone: string; is_active: boolean; deactivated_at: string | null; deactivation_reason: string | null;
  // Operating hours + the suggested session grid. session_start_times is the raw Json? column,
  // so it is typed unknown and read through formatSessionTimes/readSessionTimes rather than
  // trusted to be string[] — a hand-written row could hold anything.
  operating_hours_start: string | null; operating_hours_end: string | null;
  session_start_times: unknown;
  // ⚠ RETURNED BY /api/bases/admin ONLY. GET /api/bases omits both (0.12.7 Part E — it is written
  // to every device's offline cache), which is why they are OPTIONAL on this shared row type
  // rather than `string | null`: absent on one route, present on the other.
  phone?: string | null; email?: string | null;
}

export interface StepUp { password: string; totp?: string }
export interface DeactivateBody { reason: string; note?: string; step_up?: StepUp }

// ── Bases ──────────────────────────────────────────────────────────────────────
export async function listBases(includeInactive = false): Promise<AdminBase[]> {
  const r = await apiFetch<AdminBase[]>(`/api/bases${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
// 0.12.7 Part E: the FULL base row (phone / email / address included) for the admin Bases manager.
// GET /api/bases no longer returns those three, because it is written to every device's offline
// cache; this route is admin-only and online-only.
export async function listBasesAdmin(includeInactive = false): Promise<AdminBase[]> {
  const r = await apiFetch<AdminBase[]>(`/api/bases/admin${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function createBase(body: Record<string, unknown>): Promise<Result<AdminBase>> {
  return unwrap(await apiFetch<AdminBase>('/api/bases', { method: 'POST', body: JSON.stringify(body) }));
}
export async function updateBase(id: string, body: Record<string, unknown>): Promise<Result<AdminBase>> {
  return unwrap(await apiFetch<AdminBase>(`/api/bases/${id}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function reactivateBase(id: string): Promise<Result<AdminBase>> {
  return unwrap(await apiFetch<AdminBase>(`/api/bases/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) }));
}

// Closure preview + commit.
export interface ClosurePreview {
  base: { id: string; name: string; short_code: string; icao: string | null };
  other_active_bases: { id: string; name: string; short_code: string; icao: string | null }[];
  resources: { kind: 'aircraft' | 'asset' | 'user'; id: string; label: string; collision_base_ids?: string[] }[];
  bookings_to_orphan: number;
}
export interface ClosureResolution { kind: 'aircraft' | 'asset' | 'user'; id: string; action: 'reallocate' | 'deactivate'; target_base_id?: string; reason?: string; note?: string }
export interface ClosureSummary {
  reallocated: { kind: string; id: string; target_base_id: string }[];
  deactivated: { kind: string; id: string }[];
  bookings_orphaned: number; sessions_revoked: number; warnings: string[];
}
export async function closurePreview(id: string): Promise<Result<ClosurePreview>> {
  return unwrap(await apiFetch<ClosurePreview>(`/api/bases/${id}/closure-preview`));
}
export async function closeBase(id: string, body: { reason: string; note?: string; resolutions: ClosureResolution[]; step_up?: StepUp }): Promise<Result<{ base: AdminBase; summary: ClosureSummary }>> {
  return unwrap(await apiFetch<{ base: AdminBase; summary: ClosureSummary }>(`/api/bases/${id}`, { method: 'DELETE', body: JSON.stringify(body) }));
}

// ── Aircraft ─────────────────────────────────────────────────────────────────
export async function listAircraft(includeInactive = false): Promise<AdminAircraft[]> {
  const r = await apiFetch<AdminAircraft[]>(`/api/aircraft${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function createAircraft(body: Record<string, unknown>): Promise<Result<AdminAircraft>> {
  return unwrap(await apiFetch<AdminAircraft>('/api/aircraft', { method: 'POST', body: JSON.stringify(body) }));
}
export async function updateAircraft(reg: string, body: Record<string, unknown>): Promise<Result<AdminAircraft>> {
  return unwrap(await apiFetch<AdminAircraft>(`/api/aircraft/${encodeURIComponent(reg)}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function aircraftImpact(reg: string): Promise<number> {
  const r = await apiFetch<{ count: number }>(`/api/aircraft/${encodeURIComponent(reg)}/orphan-impact`);
  return r.data?.count ?? 0;
}
export async function deactivateAircraft(reg: string, body: DeactivateBody): Promise<Result<{ orphaned: { count: number } }>> {
  return unwrap(await apiFetch(`/api/aircraft/${encodeURIComponent(reg)}/deactivate`, { method: 'POST', body: JSON.stringify(body) }));
}
export async function reactivateAircraft(reg: string): Promise<Result<unknown>> {
  return unwrap(await apiFetch(`/api/aircraft/${encodeURIComponent(reg)}/reactivate`, { method: 'POST' }));
}

// ── Assets ───────────────────────────────────────────────────────────────────
export async function listAssets(includeInactive = false): Promise<AdminAsset[]> {
  const r = await apiFetch<AdminAsset[]>(`/api/assets${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function createAsset(body: Record<string, unknown>): Promise<Result<AdminAsset>> {
  return unwrap(await apiFetch<AdminAsset>('/api/assets', { method: 'POST', body: JSON.stringify(body) }));
}
export async function updateAsset(id: string, body: Record<string, unknown>): Promise<Result<AdminAsset>> {
  return unwrap(await apiFetch<AdminAsset>(`/api/assets/${id}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function assetImpact(id: string): Promise<number> {
  const r = await apiFetch<{ count: number }>(`/api/assets/${id}/orphan-impact`);
  return r.data?.count ?? 0;
}
export async function deactivateAsset(id: string, body: DeactivateBody): Promise<Result<{ orphaned: { count: number } }>> {
  return unwrap(await apiFetch(`/api/assets/${id}/deactivate`, { method: 'POST', body: JSON.stringify(body) }));
}
export async function reactivateAsset(id: string): Promise<Result<unknown>> {
  return unwrap(await apiFetch(`/api/assets/${id}/reactivate`, { method: 'POST' }));
}

// ── Staff / Students (users) ───────────────────────────────────────────────────
export async function listStaff(includeInactive = false): Promise<AdminStaff[]> {
  const r = await apiFetch<AdminStaff[]>(`/api/staff${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function listStudents(includeInactive = false): Promise<AdminStudent[]> {
  const r = await apiFetch<AdminStudent[]>(`/api/students${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export interface AdminUserDetail {
  id: string; email: string; full_name: string;
  // Structured names (Phase 2) — prefill the edit form; company is read-only carry-through here.
  first_name: string | null; last_name: string | null; company: string | null;
  preferred_name: string | null; pilot_code: string | null;
  mobile: string | null; training_stage: string | null; arn: string | null; arn_pending: boolean;
  home_base_id: string | null; current_base_id: string | null; is_active: boolean; roles: string[];
  // Staff "ask first" / PPR (Item 3) — prefill for the toggle; reason rides in the allocation notes.
  approval_required: boolean; approval_note: string | null;
  // PR-000059 — ops-board chip colour. NULL means the board DERIVES one from the id.
  ui_color: string | null;
}
export async function getUser(id: string): Promise<Result<{ user: AdminUserDetail }>> {
  return unwrap(await apiFetch<{ user: AdminUserDetail }>(`/api/admin/users/${id}`));
}
export async function createUser(body: Record<string, unknown>): Promise<ScopedCreateResult> {
  const r = await apiFetch<{ user: { id: string }; temp_password: string }>(`/api/admin/users`, { method: 'POST', body: JSON.stringify(body) });
  if (r.error?.code === 'NAME_DUPLICATE') {
    const matches = ((r.error.details as { matches?: DuplicateMatch[] } | undefined)?.matches ?? []);
    return { data: null, error: r.error.message, warnings: [], duplicates: matches };
  }
  return unwrap(r);
}
// A name-duplicate match surfaced by the create endpoints (Phase 5) — PII-light (id + label only).
export interface DuplicateMatch { id: string; label: string }
export type ScopedCreateResult =
  Result<{ user: { id: string }; temp_password: string }> & { duplicates?: DuplicateMatch[] };

// Phase 2 — scoped student/client create, available to ALL staff (POST /api/staff/users).
// Role-locked to student|client server-side; never accepts a roles[] or privilege field. Phase 5:
// a 409 NAME_DUPLICATE surfaces the match(es) as `duplicates` (advisory) so the modal can confirm.
export async function createScopedUser(body: Record<string, unknown>): Promise<ScopedCreateResult> {
  const r = await apiFetch<{ user: { id: string }; temp_password: string }>(`/api/staff/users`, { method: 'POST', body: JSON.stringify(body) });
  if (r.error?.code === 'NAME_DUPLICATE') {
    const matches = ((r.error.details as { matches?: DuplicateMatch[] } | undefined)?.matches ?? []);
    return { data: null, error: r.error.message, warnings: [], duplicates: matches };
  }
  return unwrap(r);
}
export async function updateUser(id: string, body: Record<string, unknown>): Promise<Result<unknown>> {
  return unwrap(await apiFetch(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function userImpact(id: string): Promise<number> {
  const r = await apiFetch<{ count: number; requires_step_up: boolean }>(`/api/admin/users/${id}/orphan-impact`);
  return r.data?.count ?? 0;
}
// Count + whether step-up is required (staff) vs confirm-only (student) — drives the dialog.
export async function userDeactivatePreview(id: string): Promise<{ count: number; requires_step_up: boolean }> {
  const r = await apiFetch<{ count: number; requires_step_up: boolean }>(`/api/admin/users/${id}/orphan-impact`);
  return { count: r.data?.count ?? 0, requires_step_up: r.data?.requires_step_up ?? true };
}
export async function deactivateUser(id: string, body: DeactivateBody): Promise<Result<{ orphaned: { count: number }; sessions_revoked: number }>> {
  return unwrap(await apiFetch(`/api/admin/users/${id}/deactivate`, { method: 'POST', body: JSON.stringify(body) }));
}
export async function reactivateUser(id: string): Promise<Result<unknown>> {
  return unwrap(await apiFetch(`/api/admin/users/${id}/reactivate`, { method: 'POST' }));
}

// ── Booking kinds (booking types) ───────────────────────────────────────────────
export interface AdminBookingKind {
  id: string; name: string; ui_color: string;
  is_maintenance: boolean; is_unavailability: boolean; is_active: boolean;
}
export async function listBookingKinds(includeInactive = false): Promise<AdminBookingKind[]> {
  const r = await apiFetch<AdminBookingKind[]>(`/api/booking-kinds${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function createBookingKind(body: Record<string, unknown>): Promise<Result<AdminBookingKind>> {
  return unwrap(await apiFetch<AdminBookingKind>('/api/booking-kinds', { method: 'POST', body: JSON.stringify(body) }));
}
export async function updateBookingKind(id: string, body: Record<string, unknown>): Promise<Result<AdminBookingKind>> {
  return unwrap(await apiFetch<AdminBookingKind>(`/api/booking-kinds/${id}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function retireBookingKind(id: string): Promise<Result<AdminBookingKind>> {
  return unwrap(await apiFetch<AdminBookingKind>(`/api/booking-kinds/${id}`, { method: 'DELETE' }));
}
export async function reactivateBookingKind(id: string): Promise<Result<AdminBookingKind>> {
  return unwrap(await apiFetch<AdminBookingKind>(`/api/booking-kinds/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) }));
}
export async function bookingKindImpact(id: string): Promise<number> {
  const r = await apiFetch<{ count: number }>(`/api/booking-kinds/${id}/impact`);
  return r.data?.count ?? 0;
}

// ── Training activities (the booking "Activity" list) ───────────────────────────
// Backend has carried full CRUD since the module landed; 0.12.5 is the SCREEN only. Same shape as
// booking kinds above: list incl. inactive (admin), create, update, DELETE = soft delete, and
// reactivate via PUT is_active:true.
//
// ⚠ NO IMPACT ENDPOINT EXISTS for training activities and one was NOT added — /api/training-
// activities has exactly four routes (GET /, POST /, PUT /:id, DELETE /:id). The retire dialog
// therefore states the consequence rather than counting rows.
//
// ⚠ default_activity_mins / default_buffer_mins are ABSENT FROM THIS INTERFACE ON PURPOSE.
// Ben ruled 2026-09-04 that they are not to be wired up (schema.prisma:1098-1109). Typing them
// here is the first step towards a field on a form, so they stop at the API boundary.
export type TrainingActivityKindValue = 'flight' | 'briefing' | 'simulator' | 'ground';
export interface AdminTrainingActivity {
  id: string; name: string; category: string | null;
  activity_kind: TrainingActivityKindValue;
  sequence_number: number | null; is_active: boolean;
}
export async function listTrainingActivities(includeInactive = false): Promise<AdminTrainingActivity[]> {
  const r = await apiFetch<AdminTrainingActivity[]>(`/api/training-activities${includeInactive ? '?include_inactive=true' : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function createTrainingActivity(body: Record<string, unknown>): Promise<Result<AdminTrainingActivity>> {
  return unwrap(await apiFetch<AdminTrainingActivity>('/api/training-activities', { method: 'POST', body: JSON.stringify(body) }));
}
export async function updateTrainingActivity(id: string, body: Record<string, unknown>): Promise<Result<AdminTrainingActivity>> {
  return unwrap(await apiFetch<AdminTrainingActivity>(`/api/training-activities/${id}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function retireTrainingActivity(id: string): Promise<Result<AdminTrainingActivity>> {
  return unwrap(await apiFetch<AdminTrainingActivity>(`/api/training-activities/${id}`, { method: 'DELETE' }));
}
export async function reactivateTrainingActivity(id: string): Promise<Result<AdminTrainingActivity>> {
  return unwrap(await apiFetch<AdminTrainingActivity>(`/api/training-activities/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) }));
}

// ── Reason codes (booking + cancellation coded lists) ────────────────────────────
export type ReasonContextValue = 'booking' | 'cancellation';
export interface AdminReasonCode {
  id: string; context: ReasonContextValue; label: string; is_active: boolean; sort_order: number;
}
export async function listReasonCodes(context?: ReasonContextValue, includeInactive = false): Promise<AdminReasonCode[]> {
  const qs = new URLSearchParams();
  if (context) qs.set('context', context);
  if (includeInactive) qs.set('include_inactive', 'true');
  const q = qs.toString();
  const r = await apiFetch<AdminReasonCode[]>(`/api/reason-codes${q ? `?${q}` : ''}`);
  return r.error || !r.data ? [] : r.data;
}
export async function createReasonCode(body: Record<string, unknown>): Promise<Result<AdminReasonCode>> {
  return unwrap(await apiFetch<AdminReasonCode>('/api/reason-codes', { method: 'POST', body: JSON.stringify(body) }));
}
export async function updateReasonCode(id: string, body: Record<string, unknown>): Promise<Result<AdminReasonCode>> {
  return unwrap(await apiFetch<AdminReasonCode>(`/api/reason-codes/${id}`, { method: 'PUT', body: JSON.stringify(body) }));
}
export async function retireReasonCode(id: string): Promise<Result<AdminReasonCode>> {
  return unwrap(await apiFetch<AdminReasonCode>(`/api/reason-codes/${id}`, { method: 'DELETE' }));
}
export async function reactivateReasonCode(id: string): Promise<Result<AdminReasonCode>> {
  return unwrap(await apiFetch<AdminReasonCode>(`/api/reason-codes/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) }));
}
export async function reasonCodeImpact(id: string): Promise<number> {
  const r = await apiFetch<{ count: number }>(`/api/reason-codes/${id}/impact`);
  return r.data?.count ?? 0;
}

// ── Orphaned bookings ──────────────────────────────────────────────────────────
export interface OrphanedBooking {
  id: string; orphaned_cause: string | null; orphaned_source: string | null; source_label: string;
  orphaned_at: string | null; resolved_at: string | null;
  base: { id: string; name: string; short_code: string } | null;
  block_start_at: string | null; dep_timezone: string; booking_status: string;
  activity_note: string | null; waitlisted: boolean; lost_label: string;
}
export async function listOrphans(): Promise<OrphanedBooking[]> {
  const r = await apiFetch<OrphanedBooking[]>('/api/admin/orphans');
  return r.error || !r.data ? [] : r.data;
}
export interface OrphanResolveBody {
  action: 'reallocate' | 'cancel';
  base_id?: string; aircraft_registration?: string; asset_id?: string; instructor_user_id?: string;
  cancel_reason?: string;
}
export async function resolveOrphan(taskId: string, body: OrphanResolveBody): Promise<Result<unknown>> {
  return unwrap(await apiFetch(`/api/admin/orphans/${taskId}/resolve`, { method: 'POST', body: JSON.stringify(body) }));
}
